// User-facing German texts that are referenced from several places or are
// likely to be refined later (error messages, ChatGPT login hints).

import type { SummarizeStage, SummaryLang } from '../../shared/api';
import type { ClientErrorCode } from './api';

export const APP_NAME = 'YouTube Summary';

export const STAGE_LABELS: Record<SummarizeStage, string> = {
  cache: 'Cache prüfen',
  transcript: 'Transkript laden',
  summarizing: 'Analysieren',
  writing: 'Schreiben',
};

/** German names of the summary languages ("auf Englisch"). */
export const LANG_NAMES: Record<SummaryLang, string> = {
  de: 'Deutsch',
  en: 'Englisch',
  es: 'Spanisch',
  nl: 'Niederländisch',
};

const EFFORT_NAMES: Record<string, string> = {
  none: 'Keine',
  minimal: 'Minimal',
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  xhigh: 'Sehr hoch',
  max: 'Maximal',
  ultra: 'Ultra',
};

/** German name of a reasoning effort (one this app does not know yet: as the backend calls it). */
export function effortName(effort: string): string {
  return EFFORT_NAMES[effort] ?? effort;
}

/** "von" in the summary language, as used in the summary heading ("### von <Creator>"). */
export const BY_WORDS: Record<SummaryLang, string> = { de: 'von', en: 'by', es: 'de', nl: 'van' };

/** Error codes shown to the user: API/client codes plus browser-side transcript failures. */
export type JobErrorCode = ClientErrorCode | 'transcript_browser' | 'transcript_blocked';

export const ERROR_TEXTS: Record<Exclude<JobErrorCode, 'aborted'>, { title: string; message: string }> = {
  codex_auth_required: {
    title: 'ChatGPT ist noch nicht verbunden',
    message:
      'Die Zusammenfassung wird mit deinem eigenen ChatGPT-Abo erstellt. Verbinde dein Konto einmalig – danach geht es automatisch weiter.',
  },
  codex_auth_invalid: {
    title: 'ChatGPT-Anmeldung abgelaufen',
    message:
      'Deine gespeicherte ChatGPT-Anmeldung ist nicht mehr gültig (z. B. weil du dich abgemeldet oder das Passwort geändert hast). Bitte verbinde dein Konto neu.',
  },
  codex_quota: {
    title: 'ChatGPT-Limit erreicht',
    message:
      'Dein ChatGPT-Abo hat sein Nutzungslimit für Codex vorerst erreicht. Das Limit setzt sich nach einiger Zeit von selbst zurück – versuche es später noch einmal oder verbinde ein anderes Konto.',
  },
  login_required: {
    title: 'Anmeldung erforderlich',
    message:
      'Das Transkript kann nur für angemeldete Nutzer auf dem Server geladen werden. Melde dich an oder lade das Transkript direkt in deinem Browser.',
  },
  transcript_unavailable: {
    title: 'Kein Transkript verfügbar',
    message:
      'Für dieses Video gibt es keine Untertitel – weder manuell noch automatisch erstellt. Ohne Transkript kann leider keine Zusammenfassung erstellt werden.',
  },
  transcript_browser: {
    title: 'Transkript konnte nicht geladen werden',
    message: 'Dein Browser konnte das Transkript dieses Videos nicht abrufen.',
  },
  transcript_blocked: {
    title: 'Transkript gerade nicht abrufbar',
    message: 'YouTube blockiert gerade Anfragen von deiner Verbindung – bitte später erneut versuchen.',
  },
  rate_limited: {
    title: 'Zu viele Anfragen',
    message: 'Zu viele Anfragen – bitte kurz warten und dann erneut versuchen.',
  },
  youtube_blocked: {
    title: 'YouTube blockiert den Server',
    message: 'YouTube lässt den Server gerade keine Transkripte laden. Lade das Transkript stattdessen in deinem Browser.',
  },
  video_not_found: {
    title: 'Video nicht gefunden',
    message: 'Dieses Video gibt es nicht oder es ist privat. Prüfe den Link und versuche es erneut.',
  },
  bad_request: {
    title: 'Anfrage ungültig',
    message: 'Der Server konnte die Anfrage nicht verarbeiten.',
  },
  internal: {
    title: 'Etwas ist schiefgelaufen',
    message: 'Auf dem Server ist ein unerwarteter Fehler aufgetreten. Bitte versuche es noch einmal.',
  },
  network: {
    title: 'Keine Verbindung',
    message: 'Der Server ist gerade nicht erreichbar. Prüfe deine Internetverbindung und versuche es erneut.',
  },
  stream_interrupted: {
    title: 'Verbindung unterbrochen',
    message: 'Die Verbindung zum Server ist während der Zusammenfassung abgebrochen. Bitte versuche es erneut.',
  },
  job_not_found: {
    title: 'Zusammenfassung nicht mehr verfügbar',
    message:
      'Die laufende Zusammenfassung ist auf dem Server nicht mehr vorhanden (z. B. nach einem Neustart). Bitte starte sie erneut.',
  },
};

/** Progress notes while a running summary re-attaches to its server-side job. */
export const RECONNECT_TEXTS = {
  /** The connection dropped (app minimized, network change); the job continues on the server. */
  reconnecting: 'Verbindung unterbrochen – wird automatisch fortgesetzt …',
  /** After reloading the page: attaching to the job that kept running on the server. */
  resuming: 'Verbinde mit der laufenden Zusammenfassung …',
} as const;

/** ChatGPT (Codex) login dialog. */
export const CODEX_TEXTS = {
  title: 'Mit ChatGPT verbinden',
  intro:
    'Die Zusammenfassungen erstellt ChatGPT mit deinem eigenen Abo (Plus, Pro, Business …). Du verbindest dein Konto nur einmal.',
  storedInBrowser: 'Die Anmeldung wird nur in diesem Browser gespeichert.',
  storedInAccount: 'Die Anmeldung wird verschlüsselt in deinem Konto gespeichert.',
  // Device code (primary)
  deviceSetupTitle: 'Erstes Mal? Gerätecode in ChatGPT erlauben',
  deviceSetupWorkspace:
    'Bei Business- oder Workspace-Konten muss ein Admin das erlauben. Alternativ: „Weitere Möglichkeiten“ → Anmeldung per Weiterleitungs-URL.',
  chatGptSecurityUrl: 'https://chatgpt.com/#settings/Security',
  deviceStep1: 'Öffne die ChatGPT-Anmeldung:',
  deviceStep2: 'Gib dort diesen Code ein:',
  deviceWarning:
    'OpenAI warnt dort evtl. vor Codes, die dir eine Website gegeben hat. Das ist hier erwartet: Die App nutzt die Codex-Anmeldung in deinem Namen. Fahre nur fort, wenn du sie selbst gestartet hast.',
  deviceWaiting: 'Warte auf Bestätigung bei ChatGPT\u00a0…',
  deviceExpired: 'Der Code ist abgelaufen. Fordere einfach einen neuen an.',
  deviceDenied: 'Die Anmeldung wurde bei ChatGPT abgelehnt.',
  // Alternatives
  moreOptions: 'Weitere Möglichkeiten',
  browserTitle: 'Anmeldung per Weiterleitungs\u2011URL',
  browserSubtitle: 'Ohne Einstellung in ChatGPT',
  browserIntro: 'Du meldest dich direkt bei ChatGPT an und kopierst danach eine Adresse hierher.',
  browserStep2:
    'Danach landet dein Browser auf einer Seite, die nicht lädt (Adresse beginnt mit http://127.0.0.1:1455/…). Das ist richtig so – kopiere die komplette Adresse aus der Adresszeile.',
  authJsonTitle: 'auth.json einfügen',
  authJsonSubtitle: 'Aus der Codex CLI',
  authJsonRotation:
    'Die Tokens werden beim Erneuern ausgetauscht: Sobald diese App sie zum ersten Mal erneuert, musst du dich in der Codex CLI neu anmelden. Besser ist eine eigene Anmeldung per Code oder Weiterleitungs-URL.',
  authJsonWarning: 'Die Datei enthält Zugangsdaten zu deinem ChatGPT-Konto. Teile sie nur mit Diensten, denen du vertraust.',
  migrateOffer:
    'Deine ChatGPT-Verbindung ist bisher nur in diesem Browser gespeichert. In dein Konto übernehmen, damit sie auf all deinen Geräten funktioniert?',
} as const;

/** "Zu viele Anfragen – bitte kurz warten" with the wait time when known. */
export function rateLimitText(retryAfter: number | undefined): string {
  if (!retryAfter || retryAfter <= 0) return ERROR_TEXTS.rate_limited.message;
  const wait = retryAfter < 90 ? `${retryAfter} Sekunden` : `${Math.ceil(retryAfter / 60)} Minuten`;
  return `Zu viele Anfragen – bitte etwa ${wait} warten und dann erneut versuchen.`;
}

/** German text for a failed Codex login (`message` is the server's English detail). */
export function codexLoginFailureText(
  status: 'expired' | 'denied' | 'error',
  message: string | undefined,
  method: 'device' | 'browser',
): string {
  const detail = message ?? '';
  if (/entitlement|not enabled for this/i.test(detail)) {
    return 'Codex ist für diesen ChatGPT-Workspace nicht freigeschaltet. Bitte den Admin deines Workspaces, es zu erlauben.';
  }
  if (status === 'expired') {
    return method === 'device' ? CODEX_TEXTS.deviceExpired : 'Die Anmeldung ist abgelaufen. Starte sie bitte neu.';
  }
  if (status === 'denied') return CODEX_TEXTS.deviceDenied;
  if (/another session/i.test(detail)) {
    return method === 'device'
      ? 'Die Anmeldung wurde in einer anderen Sitzung gestartet (z. B. vor dem Anmelden in der App). Fordere bitte einen neuen Code an.'
      : 'Die Anmeldung wurde in einer anderen Sitzung gestartet (z. B. vor dem Anmelden in der App). Starte sie bitte neu.';
  }
  if (/different sign-in|unknown or invalid login/i.test(detail)) {
    return method === 'device'
      ? 'Diese Anmeldung ist nicht mehr gültig. Fordere bitte einen neuen Code an.'
      : 'Diese Adresse gehört zu einem anderen Anmeldeversuch. Starte die Anmeldung neu und füge danach die neue Adresse ein.';
  }
  if (/no sign-in code|complete address/i.test(detail)) {
    return 'In der Adresse fehlt der Anmeldecode. Kopiere bitte die komplette Adresse aus der Adresszeile.';
  }
  if (/reach|unreachable|network/i.test(detail)) {
    return 'Der Anmeldeserver von OpenAI ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal.';
  }
  return 'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.';
}

export function planLabel(plan: string | undefined): string | undefined {
  if (!plan) return undefined;
  const known: Record<string, string> = {
    plus: 'Plus',
    pro: 'Pro',
    team: 'Team',
    business: 'Business',
    enterprise: 'Enterprise',
    edu: 'Edu',
    free: 'Free',
  };
  return known[plan.toLowerCase()] ?? plan.charAt(0).toUpperCase() + plan.slice(1);
}
